// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { joyoKanji } from "./joyoKanji";
import { jinmeiyoKanji } from "./jinmeiyoKanji";
import { joyoKanjiMeaningsWithJlpt, allKanjiMeanings, totalKanjiCount } from "./allKanjiMeanings";

export const blogPosts = [
    {
        slug: "joyo-kanji-list",
        title: "The Full Jōyō Kanji List, With Meanings",
        date: "2026-08-27",
        excerpt:
            `All ${joyoKanji.length.toLocaleString()} official jōyō kanji in one place, each paired with its ` +
            "common English meaning - browse the complete list in more detail than the lessons page has room for.",
        tags: ["Kanji", "Reference"],
        content: [
            {
                text:
                    `The jōyō (常用, "regular use") kanji are the ${joyoKanji.length.toLocaleString()} characters ` +
                    "officially designated by Japan's Ministry of Education for use in laws, newspapers, and " +
                    "everyday publications, in a list last revised in 2010. They're taught throughout compulsory " +
                    "education, and together they're what a fluent adult reader is expected to recognize without " +
                    "furigana.",
            },
            {
                text:
                    "Unlike hiragana and katakana, kanji represent meaning rather than just sound, and most have " +
                    "several possible readings depending on context. The list below pairs every jōyō kanji with " +
                    "one of its common English meanings, so you can browse it as a reference rather than just a " +
                    "wall of characters.",
            },
            { heading: `All ${joyoKanji.length.toLocaleString()} kanji, with meanings` },
            {
                text:
                    "Use the filters below to narrow the list down to a single JLPT level - handy if you're " +
                    "studying toward a specific exam rather than browsing the whole set at once.",
            },
            { kanjiMeaningGrid: joyoKanjiMeaningsWithJlpt },
            {
                parts: [
                    "This list only covers meaning, not pronunciation - most jōyō kanji have both an on'yomi " +
                    "(Chinese-derived) and kun'yomi (native Japanese) reading, which is a separate thing to " +
                    "learn for each one. For an introduction to how kanji fit alongside hiragana and katakana, " +
                    "see the ",
                    { to: "/lessons", label: "lessons page" },
                    ", or try the ",
                    { to: "/converter", label: "hiragana-to-kanji converter" },
                    " to see which kanji are commonly used to write everyday words.",
                ],
            },
        ],
    },
    {
        slug: "full-kanji-list",
        title: "The Full Kanji List: Jōyō and Jinmeiyō, With Meanings",
        date: "2026-08-28",
        excerpt:
            `Every kanji in Kana Learn's reference - all ${totalKanjiCount.toLocaleString()} of them - in one ` +
            "browsable list, covering the jōyō kanji plus the jinmeiyō kanji used in names.",
        tags: ["Kanji", "Reference"],
        content: [
            {
                text:
                    `This list combines both kanji sets covered on this site: the ${joyoKanji.length.toLocaleString()} ` +
                    "jōyō (常用, \"regular use\") kanji taught in compulsory education and used throughout everyday " +
                    `writing, and the ${jinmeiyoKanji.length.toLocaleString()} jinmeiyō (人名用, "for use in personal ` +
                    "names\") kanji, which are approved for names but aren't part of the standard curriculum.",
            },
            {
                text:
                    "Together they add up to every kanji you'll find on a detail page in Kana Learn. Since jinmeiyō " +
                    "kanji aren't tested on the JLPT, they only show up when browsing \"All\" below rather than " +
                    "under a specific level.",
            },
            { heading: `All ${totalKanjiCount.toLocaleString()} kanji, with meanings` },
            {
                text:
                    "Use the filters below to narrow the list down to a single JLPT level, or browse everything " +
                    "at once with \"All\".",
            },
            { kanjiMeaningGrid: allKanjiMeanings },
            {
                parts: [
                    "Looking for just the jōyō kanji on their own? See the ",
                    { to: "/blog/joyo-kanji-list", label: "jōyō kanji list" },
                    ", or head to the ",
                    { to: "/lessons", label: "lessons page" },
                    " for an introduction to how kanji fit alongside hiragana and katakana.",
                ],
            },
        ],
    },
    {
        slug: "hiragana-vs-katakana",
        title: "Hiragana vs Katakana: What's the Difference?",
        date: "2026-08-27",
        excerpt:
            "Hiragana and katakana cover the exact same sounds, so why does Japanese bother with two scripts? " +
            "Here's how to tell them apart at a glance and know which one a word should use.",
        tags: ["Kana", "Beginner"],
        content: [
            {
                text:
                    "If you're new to Japanese, the two kana scripts can feel like a strange redundancy: hiragana " +
                    "(ひらがな) and katakana (カタカナ) represent the exact same set of sounds, just with different " +
                    "shapes. They're not alternatives you pick between, though - each has its own job.",
            },
            { heading: "Spot the difference by shape" },
            {
                text:
                    "Hiragana is rounded and flowing - あ, ひ, ら curve and loop, since the script grew out of " +
                    "cursive handwriting. Katakana is angular and blocky - ア, ヒ, ラ are made of short, straight " +
                    "strokes, since each character was originally just a fragment cut from a more complex kanji. " +
                    "Once you've seen a few side by side, the two styles are easy to tell apart even before you " +
                    "can read them.",
            },
            { heading: "Spot the difference by use" },
            {
                list: [
                    "Hiragana writes native Japanese words, grammar particles (は, を, に…), and verb/adjective endings.",
                    "Katakana writes foreign loanwords (コーヒー, kōhī, \"coffee\"), foreign names, and onomatopoeia.",
                    "Katakana is also used for emphasis, similar to italics in English.",
                ],
            },
            {
                text:
                    "A simple rule of thumb: if a word came from English, French, or another foreign language " +
                    "relatively recently, it's almost certainly written in katakana. Everything else - the " +
                    "grammatical glue of a sentence especially - is hiragana.",
            },
            {
                parts: [
                    "Because they map one-to-one, once you've learned hiragana, learning katakana is mostly just " +
                    "memorizing a second set of shapes for sounds you already know. Try both scripts yourself " +
                    "with the practice keyboard, or read the full breakdown in the ",
                    { to: "/lessons", label: "hiragana and katakana lessons" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "type-japanese-without-japanese-keyboard",
        title: "How to Type Japanese Without a Japanese Keyboard",
        date: "2026-08-20",
        excerpt:
            "You don't need a Japanese IME installed or a special keyboard layout to write in kana. " +
            "Romaji input lets you type Japanese sounds with the keys you already have.",
        tags: ["Typing", "Tools"],
        content: [
            {
                text:
                    "Most people trying to write Japanese for the first time assume they need to install an " +
                    "input method editor (IME) or switch their whole keyboard layout. You don't - the standard " +
                    "way Japanese speakers themselves type kana on a regular QWERTY keyboard is romaji input, " +
                    "and it works the same way here.",
            },
            { heading: "How romaji input works" },
            {
                text:
                    "Romaji input converts the Latin letters you type into the kana that sound the same. Type " +
                    "\"ka\" and you get か; type \"kya\" and you get きゃ. There's no need to memorize where each " +
                    "kana lives on some special layout - you're just spelling out the sound.",
            },
            {
                list: [
                    "Single vowels: a, i, u, e, o → あ, い, う, え, お",
                    "Consonant + vowel: ka, shi, tsu, etc. → か, し, つ",
                    "Combination sounds: kya, sho, ryu, etc. → きゃ, しょ, りゅ",
                    "Doubled consonants: type the consonant twice, e.g. \"kitte\" → きって (the small っ)",
                    "The nasal ん: type \"n\" (or \"nn\" before a vowel, to avoid it merging into na/ni/nu/ne/no)",
                ],
            },
            { heading: "Try it directly in the browser" },
            {
                parts: [
                    "The ",
                    { to: "/keyboard", label: "on-screen keyboard" },
                    " on this site supports exactly this: type romaji into the input field and watch it convert " +
                    "to hiragana or katakana in real time, or click characters directly if you'd rather not type " +
                    "romaji at all. There's nothing to install, and nothing to configure.",
                ],
            },
            {
                parts: [
                    "Already have hiragana and want the kanji it's usually written with? The ",
                    { to: "/converter", label: "hiragana-to-kanji converter" },
                    " suggests common kanji for beginner vocabulary as you type.",
                ],
            },
        ],
    },
    {
        slug: "tips-to-memorize-kana-faster",
        title: "5 Tips for Memorizing Hiragana and Katakana Faster",
        date: "2026-08-13",
        excerpt:
            "92 characters between the two scripts sounds like a lot, but most learners have both down within " +
            "a couple of weeks using the right approach. Here's what actually works.",
        tags: ["Study Tips", "Beginner"],
        content: [
            {
                text:
                    "Hiragana and katakana together are 46 characters each, plus voiced and combination forms - " +
                    "but unlike kanji, that's a fixed, finite set you only need to learn once. Here's how to get " +
                    "there quickly.",
            },
            { heading: "1. Learn hiragana completely before starting katakana" },
            {
                text:
                    "It's tempting to study both at once since they represent the same sounds, but mixing them " +
                    "early just doubles the chance of confusing similar-looking characters. Get hiragana solid " +
                    "first - katakana goes much faster afterward since you're only learning new shapes, not new sounds.",
            },
            { heading: "2. Drill by row, not alphabetically" },
            {
                text:
                    "Learn the five vowels first, then one consonant row at a time (か・き・く・け・こ, then " +
                    "さ・し・す・せ・そ, and so on). This mirrors how the characters are actually organized " +
                    "(the gojūon grid) and makes the voiced forms (が, ざ, だ…) trivial to pick up afterward, " +
                    "since they're just the same rows with a dakuten added.",
            },
            { heading: "3. Write each character by hand" },
            {
                parts: [
                    "Typing and reading are useful, but the muscle memory from actually drawing a character - " +
                    "even just a few times on paper - makes it stick far faster than recognition alone. The ",
                    { to: "/kana-writing-practice", label: "kana writing practice" },
                    " page animates each hiragana and katakana character's stroke order and lets you trace it " +
                    "yourself.",
                ],
            },
            { heading: "4. Use mnemonics for the ones that don't stick" },
            {
                text:
                    "Most learners get through 80% of kana quickly and then get stuck on a handful of " +
                    "look-alikes (ぬ/め/ね, シ/ツ, ソ/ン). A silly image tied to the shape - \"ン looks like it's " +
                    "about to fall over\" - beats pure repetition for exactly these stragglers.",
            },
            { heading: "5. Practice with real input, not just flashcards" },
            {
                parts: [
                    "Recognizing a character passively is a different skill from producing it. Typing real " +
                    "words on the ",
                    { to: "/keyboard", label: "practice keyboard" },
                    " - or working through the ",
                    { to: "/lessons", label: "lessons" },
                    " character by character - forces active recall, which is what actually transfers to " +
                    "reading real Japanese text.",
                ],
            },
        ],
    },
    {
        slug: "translate-japanese-from-a-photo",
        title: "How to Translate Japanese From a Photo",
        date: "2026-09-05",
        excerpt:
            "Point your camera at a menu, sign, or page of Japanese text and get an English translation in " +
            "seconds - no typing, and no separate translation app to install.",
        tags: ["Translation", "Tools"],
        content: [
            {
                text:
                    "The fastest way to translate Japanese you can't type yourself is usually a photo - a " +
                    "restaurant menu, a street sign, a page from a book, or a screenshot. KanaLearn's translator " +
                    "reads the Japanese text out of a photo directly in your browser, then translates it to " +
                    "English, without you typing a single character.",
            },
            { heading: "Translating a photo, step by step" },
            {
                list: [
                    "Open the translator page.",
                    "Click \"Upload a photo instead\" and choose a photo containing Japanese text - a " +
                    "screenshot works just as well as a picture taken with a camera.",
                    "Wait a few seconds while the text is read from the image.",
                    "Check the extracted Japanese text, and fix anything that looks wrong before translating.",
                    "Click \"Translate\" to get the English translation.",
                ],
            },
            {
                parts: [
                    "Try it now on the ",
                    { to: "/translate", label: "Japanese photo translator" },
                    " - it's free, and the photo never leaves your browser during the text-reading step.",
                ],
            },
            { heading: "Why read the text out first, instead of translating the photo directly" },
            {
                text:
                    "Extracting the text before translating it means you get to see - and fix - exactly what " +
                    "was read, before it's turned into English. Optical character recognition (OCR) can " +
                    "occasionally misread a character in a blurry photo, an unusual font, or handwriting, and " +
                    "catching that at the text stage avoids a translation that reads confidently but is wrong " +
                    "for a reason you'd never spot in the English output alone.",
            },
            { heading: "Getting a photo that reads reliably" },
            {
                list: [
                    "Good, even lighting - avoid heavy glare or shadows falling across the text",
                    "Hold the camera roughly parallel to the page or sign, not at a steep angle",
                    "Fill the frame with the text rather than a lot of surrounding background",
                    "Printed text reads far more reliably than handwriting",
                ],
            },
            {
                parts: [
                    "Already have the text typed or copied somewhere? The same ",
                    { to: "/translate", label: "translator" },
                    " accepts plain Japanese text too - the photo step is only there for text you can't type " +
                    "yourself.",
                ],
            },
        ],
    },
    {
        slug: "jlpt-europe-countries-with-no-test-site",
        title: "European Countries With No JLPT Test Site (And Where to Go Instead)",
        date: "2026-09-22",
        excerpt:
            "Not every country in Europe currently has a JLPT exam site. Here's which ones are affected right " +
            "now, and the nearest place to sit the test if you live in one of them.",
        tags: ["JLPT", "Study Tips"],
        content: [
            {
                text:
                    "The JLPT's overseas test sites change from year to year - a country can lose its usual venue, " +
                    "or have it suspended for a cycle, without much warning. Right now, two countries in Europe fall " +
                    "into that gap: Portugal and Belgium. If you live in either, here's what's actually going on " +
                    "and where the nearest real alternative is.",
            },
            { heading: "Portugal: no active site" },
            {
                text:
                    "Portugal's listed JLPT site is currently marked temporarily suspended, with no session " +
                    "scheduled. The closest country with real, active sites is Spain, which runs the exam across " +
                    "several cities each year.",
            },
            { heading: "Belgium: suspended for 2026" },
            {
                text:
                    "Belgium normally runs the JLPT in Leuven and Ghent, but both are suspended for 2026. The " +
                    "nearest active alternative is the Netherlands (Leiden) - closer than the next-nearest options " +
                    "in Germany or France.",
            },
            { heading: "Finding your nearest option automatically" },
            {
                parts: [
                    "Rather than cross-checking this by hand, save your country on your ",
                    { to: "/profile", label: "profile page" },
                    " and it'll carry over to the ",
                    { to: "/jlpt", label: "JLPT test site search" },
                    ": if your country currently has no active site, the page tells you clearly and offers the " +
                    "nearest real alternative with one click - without changing the country saved on your profile, " +
                    "so you can always search anywhere else too.",
                ],
            },
            {
                text:
                    "This list reflects the site data at the time of writing and will get out of date as exam " +
                    "sites change - always confirm directly on the official JLPT site before making travel plans.",
            },
        ],
    },
    {
        slug: "learning-japanese-in-portugal-da-vinci-school",
        ptSlug: "aprender-japones-em-portugal-curso-n5",
        title: "Learning Japanese in Portugal: What a Real N5 Course Looks Like",
        date: "2026-09-22",
        excerpt:
            "Portugal doesn't currently have an active JLPT test site, but that doesn't mean there's nowhere to " +
            "study - here's a first-hand look at a real, fully remote N5 course from Da Vinci Language School.",
        tags: ["JLPT", "Study Tips"],
        content: [
            {
                parts: [
                    "As we covered in ",
                    { to: "/blog/jlpt-europe-countries-with-no-test-site", label: "our post on JLPT test sites in Europe" },
                    ", Portugal currently has no active JLPT venue. That's a real gap for taking the exam, but " +
                        "it's not the same as there being nowhere to actually learn Japanese in Portugal. One of us " +
                        "has been taking a fully remote Japanese course, so here's what that experience is " +
                        "actually like.",
                ],
            },
            { heading: "The school: Da Vinci Language School (remote, based in Faro)" },
            {
                parts: [
                    "Da Vinci Language School is run by Ginásios da Educação Da Vinci (GEDAVINCI LDA), a DGERT-" +
                        "certified training entity based in Faro, Portugal. Importantly, the Japanese course " +
                        "itself is entirely remote: live, real-time group classes over Zoom rather than pre-" +
                        "recorded video or in-person attendance. You don't need to live near Faro, or even in " +
                        "the Algarve - anyone in Portugal with a stable internet connection can join. See the " +
                        "current language courses, including Japanese, on ",
                    { href: "https://www.ginasiosdavinci.com/cursos-de-linguas/", label: "Da Vinci's own site" },
                    ".",
                ],
            },
            { heading: "How the course is structured" },
            {
                text:
                    "Beginner Japanese is split into two consecutive modules, N5.1 and N5.2, each 25 hours of " +
                    "class time delivered as two 1.5-hour sessions per week over roughly two months. Each module " +
                    "ends with a graded test, and finishing one earns a certificate. The school also runs a " +
                    "separate course listed simply as \"N5\" (no .1/.2 split) aimed specifically at JLPT exam " +
                    "preparation, distinct from the two beginner modules.",
            },
            {
                text:
                    "Pricing at the time of writing: the N5.1 module is 300€, N5.2 is 275€, and the standalone " +
                    "JLPT-prep \"N5\" course is 137.5€ - all for 25 hours of group instruction. Private lessons " +
                    "with the same teacher are also available outside the school's group courses, for anyone who " +
                    "wants a faster or more individual pace.",
            },
            { heading: "Practical things to know before signing up" },
            {
                text:
                    "A few policy details worth knowing if you're considering a similar course (these are common " +
                    "for this kind of certified group training, not unique to any one provider): attendance is " +
                    "tracked per session, and missing more than a set share of the total hours (commonly around " +
                    "10-20%) can mean you don't pass the module. Withdrawing partway through generally doesn't " +
                    "come with a refund unless the delay or cancellation is on the school's side - e.g. a group " +
                    "not reaching its minimum size. Getting the official certificate at the end usually requires " +
                    "meeting the attendance minimum and passing the course, not just finishing the calendar. " +
                    "Always check the specific school's own terms before paying, since these details vary.",
            },
            { heading: "Is this a substitute for the missing test site?" },
            {
                text:
                    "No - taking a course like this doesn't change the fact that Portugal has no active JLPT " +
                    "venue right now. If you're aiming to actually sit the exam, you'll still need to travel, and " +
                    "our JLPT test site page can point you to the nearest active alternative. What a course like " +
                    "this does solve is the more basic question of where to actually learn the language in the " +
                    "meantime, with real feedback and structure rather than studying alone.",
            },
            {
                parts: [
                    "If you're in Portugal and looking for a structured option, it's worth searching for a local " +
                        "language school offering live or in-person Japanese classes - and pairing that with the " +
                        "free tools here for daily practice: ",
                    { to: "/lessons", label: "lessons" },
                    ", ",
                    { to: "/kanji", label: "kanji" },
                    ", and ",
                    { to: "/study-materials", label: "study material recommendations" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "aprender-japones-em-portugal-curso-n5",
        title: "Aprender Japonês em Portugal: Como é realmente um Curso de N5",
        date: "2026-09-22",
        lang: "pt-PT",
        excerpt:
            "Portugal não tem atualmente um local de exame JLPT ativo, mas isso não significa que não haja onde " +
            "estudar - eis um relato em primeira mão de um curso de N5 totalmente à distância, da Da Vinci " +
            "Language School.",
        tags: ["JLPT", "Study Tips"],
        content: [
            {
                parts: [
                    "Como podes verificar aqui ",
                    {
                        to: "/blog/jlpt-europe-countries-with-no-test-site",
                        label: "no nosso artigo sobre locais de exame JLPT na Europa",
                    },
                    " , Portugal não tem atualmente nenhum local ativo para realizar o JLPT. Isso é " +
                        "um problema real para quem quer fazer o exame, mas não significa que não haja onde " +
                        "aprender japonês em Portugal. Uma de nós tem estado a frequentar um curso totalmente à " +
                        "distância, por isso aqui fica um relato honesto de como é essa experiência.",
                ],
            },
            { heading: "A escola: Da Vinci Language School (a distância, sediada em Faro)" },
            {
                parts: [
                    "A Da Vinci Language School é gerida pela Ginásios da Educação Da Vinci (GEDAVINCI LDA), " +
                        "entidade formadora certificada pela DGERT, sediada em Faro. O curso de japonês em si é " +
                        "totalmente à distância: aulas de grupo em tempo real via Zoom, e não vídeos pré-" +
                        "gravados nem presença física. Não é preciso viver perto de Faro, nem sequer no Algarve " +
                        "- qualquer pessoa em Portugal com uma ligação à internet estável pode participar. " +
                        "Podes ver os cursos de línguas atuais, incluindo japonês, no ",
                    { href: "https://www.ginasiosdavinci.com/cursos-de-linguas/", label: "site da própria Da Vinci" },
                    ".",
                ],
            },
            { heading: "Como o curso está estruturado" },
            {
                text:
                    "O japonês de iniciação está dividido em dois módulos consecutivos, N5.1 e N5.2, cada um " +
                    "com 25 horas de aulas, distribuídas em duas sessões de 1h30 por semana, ao longo de cerca " +
                    "de dois meses. Cada módulo termina com um teste avaliado, e concluir o módulo dá direito a " +
                    "certificado. A escola tem ainda um curso à parte, indicado apenas como \"N5\" (sem .1/.2), " +
                    "vocacionado especificamente para a preparação para o exame JLPT, distinto dos dois módulos " +
                    "de iniciação.",
            },
            {
                text:
                    "Preços à data desta publicação: o módulo N5.1 custa 300€, o N5.2 custa 275€, e o curso de " +
                    "preparação para exame \"N5\" custa 137,5€ - todos para 25 horas de aulas em grupo. Há " +
                    "também aulas privadas disponíveis com a mesma professora, fora dos cursos de grupo, para " +
                    "quem preferir um ritmo mais rápido ou individual.",
            },
            { heading: "Coisas práticas a saber antes de te inscreveres" },
            {
                text:
                    "Alguns pontos úteis a conhecer se estiveres a considerar um curso semelhante (comuns a " +
                    "este tipo de formação certificada em grupo, não exclusivos desta escola): a assiduidade é " +
                    "registada por sessão, e faltar a mais do que uma determinada percentagem do total de " +
                    "horas (normalmente entre 10% e 20%) pode significar não aprovação no módulo. Desistir a " +
                    "meio geralmente não dá direito a reembolso, exceto quando o atraso ou cancelamento é da " +
                    "responsabilidade da escola - por exemplo, um grupo que não atinja o número mínimo de " +
                    "participantes. Obter o certificado final costuma exigir cumprir o mínimo de assiduidade e " +
                    "ser aprovado no curso, não apenas chegar ao fim do calendário. Confirma sempre as " +
                    "condições específicas da escola antes de pagar, porque estes detalhes variam.",
            },
            { heading: "Isto substitui a falta de um local de exame?" },
            {
                text:
                    "Não - frequentar um curso como este não muda o facto de Portugal não ter atualmente " +
                    "nenhum local ativo para o JLPT. Se o objetivo é mesmo fazer o exame, vais continuar a " +
                    "precisar de viajar, e a nossa página de locais de exame JLPT pode indicar-te a alternativa " +
                    "ativa mais próxima. O que um curso como este resolve é a questão mais básica de onde " +
                    "aprender a língua entretanto, com feedback e estrutura reais, em vez de estudar sozinho/a.",
            },
            {
                parts: [
                    "Se estás em Portugal e procuras uma opção estruturada, vale a pena procurar uma escola de " +
                        "línguas local que ofereça aulas de japonês em direto ou presenciais - e combinar isso " +
                        "com as ferramentas gratuitas daqui para a prática diária: ",
                    { to: "/lessons", label: "lições" },
                    ", ",
                    { to: "/kanji", label: "kanji" },
                    " e ",
                    { to: "/study-materials", label: "recomendações de materiais de estudo" },
                    ".",
                ],
            },
        ],
    },
];

export function getBlogPostBySlug(slug) {
    return blogPosts.find((post) => post.slug === slug);
}
